// A screenshot of the pinned area with html-to-image (npm-locked, bundled; no runtime CDN). JPEG,
// scaled and re-compressed until it fits the server's cap; null when the browser can't render it
// (the pin is still saved, without a screenshot).
import { LOAN_SELECTOR } from "./subject";
import { FEEDBACK_UI_ATTR } from "./anchor";

/** The server's cap (apps/pipeline_api/src/feedback.rs MAX_SCREENSHOT_BYTES). */
export const MAX_SCREENSHOT_BYTES = 1024 * 1024;
/** The longest side of the stored image, in pixels. */
export const MAX_SIDE = 1600;

export interface Shot {
  media_type: "image/jpeg";
  /** Standard base64, no data: prefix. */
  data: string;
  width: number;
  height: number;
}

/** Decoded bytes of a base64 string. */
export function base64Bytes(b64: string): number {
  const pad = b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - pad;
}

/** The element worth capturing: small targets (an icon, a cell) widen to an ancestor with context. */
export function captureTarget(el: Element): HTMLElement | null {
  const boundary = el.closest(LOAN_SELECTOR);
  let cur: Element | null = el;
  while (cur && cur instanceof HTMLElement) {
    const r = cur.getBoundingClientRect();
    if (cur === boundary) return cur;
    if ((r.width >= 240 && r.height >= 80) || cur.tagName === "MAIN" || cur.parentElement === cur.ownerDocument.body) return cur;
    cur = cur.parentElement;
  }
  return el instanceof HTMLElement ? el : null;
}

/** Scale so the longest side is at most `MAX_SIDE` (never upscale). */
export function scaleFor(width: number, height: number, max = MAX_SIDE): number {
  const side = Math.max(width, height, 1);
  return Math.min(1, max / side);
}

type ToJpeg = (node: HTMLElement, opts: Record<string, unknown>) => Promise<string>;

export async function captureArea(el: Element, toJpeg?: ToJpeg): Promise<Shot | null> {
  const node = captureTarget(el);
  if (!node) return null;
  const r = node.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return null;
  const render: ToJpeg = toJpeg ?? ((await import("html-to-image")).toJpeg as unknown as ToJpeg);
  let scale = scaleFor(r.width, r.height);
  let quality = 0.82;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      const url = await render(node, {
        quality,
        pixelRatio: scale,
        backgroundColor: getComputedStyle(node.ownerDocument.body).backgroundColor || "#ffffff",
        filter: (n: Node) => {
          if (!(n instanceof Element)) return true;
          if (n.hasAttribute(FEEDBACK_UI_ATTR)) return false;
          // A small LO/page field can expand into a parent with deal links; remove other subjects.
          // Never capture a grid canvas while pinning an adjacent field.
          if (n !== node && n.matches("[data-feedback-grid]")) return false;
          if (n !== node && n.matches(LOAN_SELECTOR)) {
            const subject = el.closest(LOAN_SELECTOR);
            return !!subject && n.getAttribute("data-loan-id") === subject.getAttribute("data-loan-id")
              && n.getAttribute("data-core-loan-id") === subject.getAttribute("data-core-loan-id");
          }
          return true;
        },
      });
      const data = url.slice(url.indexOf(",") + 1);
      if (url.startsWith("data:image/jpeg") && base64Bytes(data) <= MAX_SCREENSHOT_BYTES) {
        return { media_type: "image/jpeg", data, width: Math.max(1, Math.round(r.width * scale)), height: Math.max(1, Math.round(r.height * scale)) };
      }
    } catch {
      return null;
    }
    scale *= 0.7;
    quality = Math.max(0.5, quality - 0.12);
  }
  return null;
}
